import { Search, SlidersHorizontal, LogIn, LogOut, Dices, Info, BadgeInfo, Heart, Check, Mail, BarChart3, Menu, Filter, X, Home, ChevronRight, Calendar as CalendarIcon, Play, Pause, Type, Layers3, Clock3 } from "lucide-react"; /* OAL-UX: 1, 4 */
import { Button } from "@/components/ui/button";
import FontSizeControl from "@/components/FontSizeControl";
import { Input } from "@/components/ui/input";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"; /* OAL-UX: 4 */
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAppUser";
import { useClerk } from "@clerk/react";
import { trackFilterUiInteraction } from "@/lib/trackSearch";
import { useBootstrapData } from "@/lib/bootstrap";
const FiltersPanel = lazy(() => import("./FiltersPanel"));
const LazyCalendar = lazy(() => import("@/components/ui/calendar").then((mod) => ({ default: mod.Calendar })));
import type { DateFilterType } from "./FiltersPanel";
import type { TimePeriod } from "./DateFilterPills";
import { useLocation } from "wouter";
import type { MasterCategory, HeaderIconConfig } from "@shared/schema";
import logoImage from "@assets/lark_icon.jpg";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";

type IconConfig = { key: string; label: string; enabled: boolean; displayOrder: number };
type HeaderBootstrapData = {
  branding?: { logoUrl: string | null; logoSize: string };
  header?: { searchPlaceholder: string; icons: IconConfig[] };
};

const LOGO_FALLBACK_WIDTH = 1024;
const LOGO_FALLBACK_HEIGHT = 590;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const REQUIRED_ICONS = ['filters'];

const DEFAULT_ICON_ORDER: IconConfig[] = [
  { key: 'share', label: 'Share', enabled: true, displayOrder: 0 },
  { key: 'stats', label: 'Stats', enabled: true, displayOrder: 1 },
  { key: 'surpriseMe', label: 'Surprise Me', enabled: true, displayOrder: 2 },
  { key: 'filters', label: 'Filters', enabled: true, displayOrder: 3 },
  { key: 'fontSize', label: 'Font Size', enabled: true, displayOrder: 5 },
  { key: 'autoScroll', label: 'Auto Scroll', enabled: true, displayOrder: 6 },
  { key: 'contact', label: 'Contact', enabled: true, displayOrder: 7 },
];

interface CategoryFilter {
  masterCategory: MasterCategory;
  rawCategories: string[];
}

interface HeaderProps {
  categories?: string[];
  categoryFilters?: CategoryFilter[];
  sources?: string[];
  selectedCategories?: string[];
  excludedCategories?: string[];
  selectedSources?: string[];
  excludedSources?: string[];
  selectedRegions?: string[];
  excludedRegions?: string[];
  onExcludeRegionToggle?: (region: string) => void;
  onFilterChange?: (selected: string[]) => void;
  onExcludeFilterChange?: (excluded: string[]) => void;
  onBulkExcludedCategoriesChange?: (categories: string[]) => void;
  onSourceFilterChange?: (sources: string[]) => void;
  onExcludeSourceToggle?: (source: string) => void;
  onBulkExcludeSourcesChange?: (sources: string[]) => void;
  onRegionFilterChange?: (regions: string[]) => void;
  onDateFilterChange?: (filter: DateFilterType, customDate: string | null) => void;
  timePeriods?: TimePeriod[];
  onTimePeriodChange?: (periods: TimePeriod[]) => void;
  onSearch?: (query: string) => void;
  searchValue?: string | null;
  onSurpriseMe?: () => void;
  surpriseMeDisabled?: boolean;
  viewMode?: 'HOME' | 'SURPRISE';
  exploreMode?: 'events' | 'sources' | 'recently-viewed';
  onExploreModeChange?: (mode: 'events' | 'sources' | 'recently-viewed') => void;
  recentlyViewedCount?: number;
  dateFilter?: DateFilterType;
  customDate?: string | null;
  isAutoScrollPlaying?: boolean;
  onAutoScrollToggle?: () => void;
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
  onFiltersApply?: () => void;
  desktopFiltersOpen?: boolean;
  onDesktopFiltersToggle?: () => void;
  activeRegionOverride?: string | null;
  onCityPillClick?: (city: string) => void;
  onClearRegionOverride?: () => void;
  mobileExpandSection?: { section: 'regions' | 'categories' | 'sources'; ts: number } | null;
  hideNavigationLayer?: boolean;
  filtersGlowPulseKey?: number;
  mobileHeaderCollapsed?: boolean;
  hideEventFilterButton?: boolean;
  filtersReturnFocusRef?: React.RefObject<HTMLButtonElement>;
}

export default function Header({ 
  categories = [], 
  categoryFilters = [],
  sources = [],
  selectedCategories = [],
  excludedCategories = [],
  selectedSources = [],
  excludedSources = [],
  selectedRegions = [],
  excludedRegions = [],
  onExcludeRegionToggle,
  onFilterChange,
  onExcludeFilterChange,
  onBulkExcludedCategoriesChange,
  onSourceFilterChange,
  onExcludeSourceToggle,
  onBulkExcludeSourcesChange,
  onRegionFilterChange,
  onDateFilterChange,
  timePeriods = ['allDay'],
  onTimePeriodChange,
  onSearch,
  searchValue,
  onSurpriseMe,
  surpriseMeDisabled = false,
  viewMode = 'HOME',
  exploreMode = 'events',
  onExploreModeChange,
  recentlyViewedCount = 0,
  dateFilter: externalDateFilter,
  customDate: externalCustomDate,
  isAutoScrollPlaying = false,
  onAutoScrollToggle,
  filtersOpen,
  onFiltersOpenChange,
  onFiltersApply,
  desktopFiltersOpen = false,
  onDesktopFiltersToggle,
  activeRegionOverride = null,
  onCityPillClick,
  onClearRegionOverride,
  mobileExpandSection = null,
  hideNavigationLayer = false,
  filtersGlowPulseKey = 0,
  mobileHeaderCollapsed = false,
  hideEventFilterButton = false,
  filtersReturnFocusRef,
}: HeaderProps) {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { signOut } = useClerk();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [location, setLocation] = useLocation();
  const [internalShowFilters, setInternalShowFilters] = useState(false);
  
  const showFilters = filtersOpen !== undefined ? filtersOpen : internalShowFilters;
  const setShowFilters = onFiltersOpenChange || setInternalShowFilters;
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied'>('idle');
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersGlowActive, setFiltersGlowActive] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const internalFiltersTriggerRef = useRef<HTMLButtonElement>(null);
  const filtersTriggerRef = filtersReturnFocusRef ?? internalFiltersTriggerRef;
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuCloseButtonRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const mobileHeaderCollapsibleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };
    
    updateHeaderHeight();
    
    window.addEventListener('resize', updateHeaderHeight);
    
    let resizeObserver: ResizeObserver | null = null;
    if (headerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(headerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      resizeObserver?.disconnect();
    };
  }, [showFilters, menuOpen, mobileHeaderCollapsed]);

  useEffect(() => {
    const collapsible = mobileHeaderCollapsibleRef.current;
    if (!collapsible) return;

    if (mobileHeaderCollapsed) {
      collapsible.setAttribute('inert', '');
    } else {
      collapsible.removeAttribute('inert');
    }

    return () => {
      collapsible.removeAttribute('inert');
    };
  }, [mobileHeaderCollapsed]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--header-offset',
      mobileHeaderCollapsed ? '0px' : 'var(--header-height, 92px)',
    );

    return () => {
      root.style.removeProperty('--header-offset');
    };
  }, [mobileHeaderCollapsed]);
  
  // Close menu when filters open
  useEffect(() => {
    if (showFilters && menuOpen) {
      setMenuOpen(false);
    }
  }, [showFilters]);
  
  // Close filters when menu opens
  useEffect(() => {
    if (menuOpen && showFilters) {
      setShowFilters(false);
    }
  }, [menuOpen]);
  
  // Lock body scroll when filters panel or menu is open
  useEffect(() => {
    if (showFilters || menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showFilters, menuOpen]);
  
  const [internalDateFilter, setInternalDateFilter] = useState<DateFilterType>(null);
  const [internalCustomDate, setInternalCustomDate] = useState<string | null>(null);
  const [headerCalendarOpenMobile, setHeaderCalendarOpenMobile] = useState(false); /* OAL-UX: 4 */
  const [headerCalendarOpenDesktop, setHeaderCalendarOpenDesktop] = useState(false); /* OAL-UX: 4 */
  const dateFilter = externalDateFilter !== undefined ? externalDateFilter : internalDateFilter;
  const customDate = externalCustomDate !== undefined ? externalCustomDate : internalCustomDate;
  
  const { data: bootstrapData } = useBootstrapData<HeaderBootstrapData | undefined>((data) => ({
    branding: data.branding,
    header: {
      searchPlaceholder: data.header.searchPlaceholder,
      icons: data.header.icons,
    },
  }));

  const headerIconConfigs = Array.isArray(bootstrapData?.header?.icons)
    ? bootstrapData.header.icons
    : DEFAULT_ICON_ORDER;
  
  const sortedIcons = [...headerIconConfigs]
    .map(icon => ({
      ...icon,
      enabled: REQUIRED_ICONS.includes(icon.key) ? true : icon.enabled
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);
  
  const isIconEnabled = (key: string) => {
    if (REQUIRED_ICONS.includes(key)) return true;
    const icon = sortedIcons.find(i => i.key === key);
    return icon?.enabled ?? true;
  };

  const { data: stats } = useQuery<{ eventCount: number; sourceCount: number }>({
    queryKey: ['/api/stats'],
    enabled: menuOpen && isIconEnabled('stats'),
  });
  
  // Persist logo URL to localStorage so subsequent loads show the correct logo immediately (no flicker)
  const [cachedLogoUrl] = useState<string | null>(() => {
    try { return localStorage.getItem('oal_logo_url'); } catch { return null; }
  });
  useEffect(() => {
    const url = bootstrapData?.branding?.logoUrl;
    if (url) {
      try { localStorage.setItem('oal_logo_url', url); } catch { /* ignore */ }
    }
  }, [bootstrapData?.branding?.logoUrl]);

  const currentLogo = bootstrapData?.branding?.logoUrl ?? cachedLogoUrl ?? logoImage;
  const logoHeight = bootstrapData?.branding?.logoSize || "48";
  const parsedLogoHeight = Number.parseInt(logoHeight, 10) || 48;
  const logoWidth = Math.max(1, Math.round((parsedLogoHeight * LOGO_FALLBACK_WIDTH) / LOGO_FALLBACK_HEIGHT));
  const searchPlaceholder = bootstrapData?.header?.searchPlaceholder || 'Search events, venues, categories...';
  
  const categoryIconMap = useMemo(() => {
    const map = new Map<string, string | null>();
    categoryFilters.forEach(cf => {
      map.set(cf.masterCategory.name.trim(), cf.masterCategory.icon || null);
    });
    return map;
  }, [categoryFilters]);
  
  const categoryColorMap = useMemo(() => {
    const map = new Map<string, string | null>();
    categoryFilters.forEach(cf => {
      map.set(cf.masterCategory.name.trim(), cf.masterCategory.color || null);
    });
    return map;
  }, [categoryFilters]);
  
  const urlParams = new URLSearchParams(location.split('?')[1] || '');
  const urlSearchQuery = urlParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  useEffect(() => {
    const currentParams = new URLSearchParams(location.split('?')[1] || '');
    const urlQuery = currentParams.get('q') || '';
    setSearchQuery(urlQuery);
  }, [location]);

  useEffect(() => {
    if (searchValue === null || searchValue === '') {
      setSearchQuery('');
    }
  }, [searchValue]);
  
  const handleSearch = useCallback((query: string) => {
    const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(currentSearch);
    
    if (query.trim()) {
      params.set('q', query);
    } else {
      params.delete('q');
    }
    
    const queryString = params.toString();
    setLocation(queryString ? `/?${queryString}` : '/');
    onSearch?.(query);
  }, [setLocation, onSearch]);
  
  const debouncedSearch = useCallback((query: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      handleSearch(query);
    }, 400);
  }, [handleSearch]);
  
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!filtersGlowPulseKey) return;

    setFiltersGlowActive(false);
    const frameId = window.requestAnimationFrame(() => {
      setFiltersGlowActive(true);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [filtersGlowPulseKey]);

  const toggleCategory = (category: string) => {
    const updated = selectedCategories.includes(category)
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    onFilterChange?.(updated);
  };

  const toggleExcludedCategory = (category: string) => {
    const updated = excludedCategories.includes(category)
      ? excludedCategories.filter(c => c !== category)
      : [...excludedCategories, category];
    onExcludeFilterChange?.(updated);
  };

  const toggleSource = (source: string) => {
    const updated = selectedSources.includes(source)
      ? selectedSources.filter(s => s !== source)
      : [...selectedSources, source];
    onSourceFilterChange?.(updated);
  };
  
  const toggleRegion = (region: string) => {
    const updated = selectedRegions.includes(region)
      ? selectedRegions.filter(r => r !== region)
      : [...selectedRegions, region];
    onRegionFilterChange?.(updated);
  };

  const handleDateFilterChange = (filter: DateFilterType, newCustomDate?: string) => {
    if (onDateFilterChange) {
      onDateFilterChange(filter, newCustomDate || null);
    } else {
      setInternalDateFilter(filter);
      setInternalCustomDate(newCustomDate || null);
    }
  };

  const clearAllFilters = () => {
    onFilterChange?.([]);
    onExcludeFilterChange?.([]);
    onSourceFilterChange?.([]);
    onRegionFilterChange?.([]);
    handleDateFilterChange(null);
    onTimePeriodChange?.(['allDay']);
    excludedSources.forEach(src => onExcludeSourceToggle?.(src));
    excludedRegions.forEach(r => onExcludeRegionToggle?.(r));
  };
  
  const hasTimeFilter = !timePeriods.includes('allDay');
  const activeFilterCount = selectedCategories.length + excludedCategories.length + selectedSources.length + excludedSources.length + selectedRegions.length + excludedRegions.length + (dateFilter ? 1 : 0) + (hasTimeFilter ? 1 : 0);
  const filterBadgeCount =
    (selectedRegions.length > 0 || excludedRegions.length > 0 ? 1 : 0) +
    (selectedCategories.length > 0 || excludedCategories.length > 0 ? 1 : 0) +
    (selectedSources.length > 0 || excludedSources.length > 0 ? 1 : 0) +
    (dateFilter ? 1 : 0) +
    (hasTimeFilter ? 1 : 0);

  const handleShare = async () => {
    const shareData = {
      title: 'OnALark - Capital District Events',
      text: 'Discover local events in Albany and the surrounding Capital District',
      url: window.location.origin,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareData.url);
        setShareStatus('copied');
        setTimeout(() => setShareStatus('idle'), 2000);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const isOnEventsPage = !location.startsWith('/about') && !location.startsWith('/contact') && !location.startsWith('/admin') && !location.startsWith('/disclaimer');
  const isFilterPanelOpen = isOnEventsPage && exploreMode === 'events' && (showFilters || desktopFiltersOpen);

  return (
    <>
      <a
        href="#event-results"
        className="sr-only fixed left-3 top-3 z-[100] rounded-full bg-white px-4 py-3 text-sm font-semibold text-foreground shadow-lg focus:not-sr-only focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
      >
        Skip to events
      </a>
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 w-full bg-background relative ${mobileHeaderCollapsed ? 'is-scrolled' : ''}`}
      data-mobile-header="true"
    >
      <div className="w-full px-4 lg:px-6">
        <div
          ref={mobileHeaderCollapsibleRef}
          className="mobile-header-collapsible"
          data-mobile-header-collapsible="true"
          aria-hidden={mobileHeaderCollapsed ? 'true' : 'false'}
        >
        {/* Responsive header: compact actions above a full-width mobile search field. */}
        <div
          className={`relative grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-2 py-2 lg:min-h-[86px] lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:gap-3 lg:rounded-[22px] lg:border lg:border-[#ece8de] lg:bg-white lg:px-4 lg:py-2.5 lg:shadow-[0_1px_4px_rgba(0,0,0,0.04)] ${isFilterPanelOpen ? 'lg:opacity-25 lg:saturate-50 lg:transition-[opacity,filter] lg:duration-200' : ''}`}
          style={{ borderBottom: '1px solid #e8e8e8' }}
          data-testid="header-main-row"
        >
          {/* Logo — shrink-0, no flex-1 so search bar can own the center */}
          <div className="col-start-1 row-start-1 m-0 flex flex-shrink-0 items-center p-0 leading-none" data-header-logo="true">
            <a
              href="/"
              className="flex min-h-[44px] items-center lg:min-h-[60px]"
              data-testid="link-home"
            >
              <img
                src={currentLogo}
                alt="On A Lark"
                width={logoWidth}
                height={parsedLogoHeight}
                style={{ '--oal-logo-max-height': `${parsedLogoHeight}px` } as React.CSSProperties}
                className="h-auto w-auto max-h-12 object-contain lg:max-h-[var(--oal-logo-max-height)]"
              />
            </a>
          </div>

          {/* Search bar — flex-1, shown when on events page and not in menu/sources mode */}
          {isOnEventsPage && !menuOpen && exploreMode === 'events' && (
            <div className="relative col-span-2 col-start-1 row-start-2 flex w-full min-w-0 items-center lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:flex-1" data-testid="search-filters-container">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder={isMobile ? "Search events…" : searchPlaceholder}
                aria-label="Search events by name, venue, location, or category"
                className="min-h-[44px] rounded-full border border-[var(--brutal-border-color)] bg-card py-3 pl-10 pr-4 text-base transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={searchQuery}
                disabled={showFilters}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                    handleSearch(searchQuery);
                  }
                }}
                data-testid="input-search"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    handleSearch('');
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Clear search"
                  data-testid="button-clear-search-input"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Right-side controls — always flex-shrink-0 */}
          <div className="col-start-2 row-start-1 flex min-w-0 flex-nowrap items-center justify-end gap-1 sm:gap-2 lg:col-start-3 lg:ml-auto lg:w-auto" data-header-actions="true">
            {/* Filters button — shown when on events page, not in menu/sources mode */}
            {!hideEventFilterButton && isOnEventsPage && !menuOpen && exploreMode === 'events' && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    ref={filtersTriggerRef}
                    onClick={() => {
                      const isDesktop = window.innerWidth >= 1024;
                      const isOpening = isDesktop ? !desktopFiltersOpen : !showFilters;
                      if (isOpening) {
                        trackFilterUiInteraction({
                          interactionType: 'filters_open',
                          surface: 'header',
                          label: 'Filters',
                        });
                      }
                      if (isDesktop) {
                        onDesktopFiltersToggle?.();
                      } else {
                        setShowFilters(!showFilters);
                      }
                    }}
                    className={`min-h-[44px] rounded-full transition-colors duration-100 ${filtersGlowActive ? 'glow-nudge' : ''}`}
                    aria-haspopup="dialog"
                    aria-expanded={showFilters}
                    aria-label={filterBadgeCount > 0
                      ? `Filters, ${filterBadgeCount} ${filterBadgeCount === 1 ? 'filter' : 'filters'} applied`
                      : 'Filters'}
                    style={{
                      backgroundColor: 'hsl(var(--green-accent))',
                      border: 'none',
                      boxShadow: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: isMobile ? '8px 14px' : '8px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      borderRadius: 20,
                    }}
                    data-testid="button-toggle-filters"
                    onAnimationEnd={(event) => {
                      if (event.target !== event.currentTarget) return;
                      setFiltersGlowActive(false);
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path
                        d="M1 2h12l-4.5 5v4l-3-1.5V7L1 2z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        fill="none"
                        opacity={1}
                      />
                    </svg>
                    <span>Filters</span>
                    {filterBadgeCount > 0 && (
                      <span
                        data-filters-badge="true"
                        aria-hidden="true"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: '#fff',
                          color: 'hsl(var(--green-accent))',
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        {filterBadgeCount}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Filter by region, category, or source</TooltipContent>
              </Tooltip>
            )}

            {/* Surprise Me button */}
            {!menuOpen && isIconEnabled('surpriseMe') && onSurpriseMe && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (surpriseMeDisabled) {
                        toast({
                          description: showFilters
                            ? "Close the filters panel to use Surprise Me"
                            : "Clear your filters to use Surprise Me",
                          duration: 3000,
                        });
                      } else {
                        onSurpriseMe();
                      }
                    }}
                    aria-disabled={surpriseMeDisabled}
                    className={`min-h-[44px] gap-2 ${surpriseMeDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="button-surprise-me"
                  >
                    <Dices className="h-5 w-5" />
                    <span className="font-medium hidden sm:inline">Surprise me</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{surpriseMeDisabled
                    ? (showFilters ? "Close the filters panel first" : "Clear your filters first")
                    : "Pick a random upcoming event"}</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Text size button — always visible in header for accessibility */}
            {!menuOpen && isIconEnabled('fontSize') && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span>
                    <FontSizeControl label="Text size" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Adjust text size</TooltipContent>
              </Tooltip>
            )}

            {/* Menu button */}
            {(
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    ref={menuTriggerRef}
                    variant="outline"
                    className="min-h-[44px] gap-2 rounded-full font-medium"
                    style={{
                      border: '1.5px solid var(--brutal-border-color)',
                      boxShadow: 'var(--brutal-shadow-sm)',
                      fontSize: 13,
                    }}
                    onClick={() => setMenuOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={menuOpen}
                    aria-label="Open menu"
                    data-testid="button-menu-toggle"
                  >
                    <Menu className="h-4 w-4" />
                    <span className="hidden sm:inline">Menu</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Settings, navigation & more</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        
        </div>

        {/* OAL-UX: 2, 3, 13 — Mode Bar (tab row, status row, LEARN MORE link) removed; replaced by new strip below */}

        {/* OAL-UX: 4 / OAL-SOFTEN: 9, 13, 15, 16 — Row 3: date pills only; Row 4: mode tabs own row */}
        {isOnEventsPage && !menuOpen && !showFilters && (
          <>
            {!hideNavigationLayer && (() => {
              const QUICK_SELECT_CITIES = ['Albany', 'Troy', 'Schenectady', 'Saratoga'] as const;
              const regionLabel = (
                <span
                  style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', flexShrink: 0 }}
                  className="shrink-0 pr-1"
                >
                  Region
                </span>
              );
              const cityPills = QUICK_SELECT_CITIES.map(city => {
                const isActive = activeRegionOverride === city;
                return (
                  <button
                    key={city}
                    type="button"
                    onClick={() => onCityPillClick?.(city)}
                    className="inline-flex min-h-[44px] items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium transition-all duration-100 shrink-0 hover:-translate-x-px hover:-translate-y-px"
                    style={isActive
                      ? { border: '1.5px solid hsl(var(--green-accent-active))', backgroundColor: 'hsl(var(--green-accent-light))', color: 'hsl(var(--foreground))', boxShadow: 'var(--brutal-shadow-sm)' }
                      : { border: '1.5px solid var(--brutal-border-color)', backgroundColor: 'transparent', color: 'hsl(var(--foreground))', boxShadow: 'var(--brutal-shadow-sm)' }
                    }
                    data-testid={`region-pill-${city.toLowerCase()}`}
                    aria-pressed={isActive}
                  >
                    {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
                    {city}
                  </button>
                );
              });
              const allRegionsBtn = (
                <button
                  type="button"
                  onClick={() => onClearRegionOverride?.()}
                  className="inline-flex min-h-[44px] items-center px-2 py-1.5 text-[13px] shrink-0 transition-colors hover:underline"
                  style={{ color: 'hsl(var(--muted-foreground))', background: 'none', border: 'none' }}
                  data-testid="region-all-regions-link"
                >
                  All regions
                </button>
              );
              const renderDatePills = (calendarOpen: boolean, setCalendarOpen: (v: boolean) => void) => (
                <>
                  {(['today', 'tomorrow', 'weekend'] as const).map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleDateFilterChange(dateFilter === val ? null : val)}
                      className="inline-flex min-h-[44px] shrink-0 items-center justify-center bg-transparent p-1.5 -m-1.5"
                      data-testid={`header-quick-${val}`}
                    >
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium transition-all duration-100 hover:-translate-x-px hover:-translate-y-px"
                        style={dateFilter === val
                          ? { border: '1.5px solid hsl(var(--green-accent-active))', backgroundColor: 'hsl(var(--green-accent-light))', color: 'hsl(var(--foreground))', boxShadow: 'var(--brutal-shadow-sm)' }
                          : { border: '1.5px solid var(--brutal-border-color)', backgroundColor: 'transparent', color: 'hsl(var(--foreground))', boxShadow: 'var(--brutal-shadow-sm)' }
                        }
                      >
                        {dateFilter === val && <Check className="h-3.5 w-3.5" />}
                        {val.charAt(0).toUpperCase() + val.slice(1)}
                      </span>
                    </button>
                  ))}
                  {dateFilter === 'custom' && customDate ? (
                    <div className="inline-flex min-h-[44px] shrink-0 items-center justify-center bg-transparent p-1.5 -m-1.5">
                      <div
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium"
                        style={{ border: '1.5px solid hsl(var(--green-accent-active))', backgroundColor: 'hsl(var(--green-accent-light))', color: 'hsl(var(--foreground))', boxShadow: 'var(--brutal-shadow-sm)' }}
                      >
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {(() => {
                          const [y, m, d] = customDate.split('-').map(Number);
                          return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        })()}
                        <button
                          type="button"
                          onClick={() => handleDateFilterChange(null)}
                          className="ml-1 inline-flex items-center justify-center rounded-full p-1"
                          aria-label="Clear date"
                          data-testid="header-clear-custom-date"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex min-h-[44px] shrink-0 items-center justify-center bg-transparent p-1.5 -m-1.5"
                          data-testid="header-pick-date"
                          onClick={() => {
                            if (dateFilter !== 'custom') handleDateFilterChange('custom');
                          }}
                        >
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium transition-all duration-100 hover:-translate-x-px hover:-translate-y-px"
                            style={{ border: '1.5px dashed var(--brutal-border-color)', backgroundColor: 'transparent', color: 'hsl(var(--foreground))', boxShadow: 'var(--brutal-shadow-sm)' }}
                          >
                            <CalendarIcon className="h-3.5 w-3.5" />
                            Pick a date
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading calendar...</div>}>
                          <LazyCalendar
                            mode="single"
                            selected={customDate ? (() => { const [y, m, d] = customDate.split('-').map(Number); return new Date(y, m - 1, d); })() : undefined}
                            onSelect={(date) => {
                              if (date) {
                                const ds = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                handleDateFilterChange('custom', ds);
                                setCalendarOpen(false);
                              }
                            }}
                          />
                        </Suspense>
                      </PopoverContent>
                    </Popover>
                  )}
                </>
              );
              return (
                <div
                  className={`mt-3 lg:mt-4 lg:rounded-[22px] lg:border lg:border-[#ece8de] lg:bg-[#fcfbf8] lg:px-4 lg:py-3 lg:shadow-[0_1px_3px_rgba(0,0,0,0.03)] ${isFilterPanelOpen ? 'lg:opacity-25 lg:saturate-50 lg:transition-[opacity,filter] lg:duration-200' : ''}`}
                  data-testid="mobile-pills-block"
                >
                  {/* Mobile (<768px): two separate stacked rows */}
                  <div className="md:hidden">
                    <div
                      className="flex items-center gap-2 py-2 overflow-x-auto"
                      style={{ borderBottom: '1px solid #e8e8e8', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      data-testid="region-quick-select-row-mobile"
                    >
                      {cityPills}
                      {allRegionsBtn}
                    </div>
                    <div className="flex flex-nowrap items-center gap-[10px] overflow-x-auto py-2" style={{ borderBottom: '1px solid #e8e8e8', scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="mode-date-strip-mobile">
                      {renderDatePills(headerCalendarOpenMobile, setHeaderCalendarOpenMobile)}
                    </div>
                  </div>
                  {/* Desktop (>=768px): single merged row with vertical divider */}
                  <div
                    className="hidden md:flex items-center gap-[10px] py-[10px] overflow-x-visible overflow-y-visible lg:border-0 lg:py-0"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    <div
                      className="flex items-center gap-2 shrink-0"
                      data-testid="region-quick-select-row"
                    >
                      {regionLabel}
                      {cityPills}
                      {allRegionsBtn}
                    </div>
                    <div style={{ width: 1, height: 22, background: 'hsl(var(--border))', flexShrink: 0, margin: '0 4px' }} />
                    <div className="flex items-center gap-[10px] flex-nowrap" data-testid="mode-date-strip">
                      {renderDatePills(headerCalendarOpenDesktop, setHeaderCalendarOpenDesktop)}
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
        <DialogPrimitive.Root
          open={isOnEventsPage && showFilters && exploreMode === 'events'}
          onOpenChange={(open) => setShowFilters(open)}
        >
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay
              className="fixed inset-0 z-[60] bg-[rgba(0,0,0,0.45)] backdrop-blur-[4px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
              data-testid="filters-overlay"
            />
            <DialogPrimitive.Content
              id="event-filters-mobile"
              className="fixed inset-x-0 bottom-0 z-[70] max-h-[calc(100dvh-env(safe-area-inset-top))] overflow-hidden outline-none"
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                window.requestAnimationFrame(() => filtersTriggerRef.current?.focus());
              }}
              data-testid="filters-dialog"
            >
              <DialogPrimitive.Title className="sr-only">Event filters</DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Refine events by region, category, source, and date.
              </DialogPrimitive.Description>
              <Suspense fallback={null}>
                <FiltersPanel
              categories={categories}
              categoryFilters={categoryFilters}
              selectedCategories={selectedCategories}
              onCategoryToggle={toggleCategory}
              excludedCategories={excludedCategories}
              onExcludedCategoryToggle={toggleExcludedCategory}
              onBulkExcludedCategoriesChange={onBulkExcludedCategoriesChange ?? onExcludeFilterChange}
              sources={sources}
              selectedSources={selectedSources}
              onSourceToggle={toggleSource}
              excludedSources={excludedSources}
              onExcludeSourceToggle={onExcludeSourceToggle}
              onBulkExcludeSourcesChange={onBulkExcludeSourcesChange}
              selectedRegions={selectedRegions}
              excludedRegions={excludedRegions}
              onRegionToggle={toggleRegion}
              onExcludeRegionToggle={onExcludeRegionToggle}
              dateFilter={dateFilter}
              customDate={customDate}
              onDateFilterChange={handleDateFilterChange}
              timePeriods={timePeriods}
              onTimePeriodChange={onTimePeriodChange || (() => {})}
              onClearAll={clearAllFilters}
              activeFilterCount={activeFilterCount}
              onClose={() => setShowFilters(false)}
              expandSection={mobileExpandSection}
                />
              </Suspense>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      <DialogPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-[60] bg-[rgba(18,24,35,0.3)] backdrop-blur-[3px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
            data-testid="menu-overlay"
          />
          <DialogPrimitive.Content
            className="fixed inset-x-0 top-3 z-[70] max-h-[calc(100dvh-24px-env(safe-area-inset-top))] overflow-y-auto px-4 pb-4 pt-[env(safe-area-inset-top)] outline-none lg:px-6"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              window.requestAnimationFrame(() => menuCloseButtonRef.current?.focus());
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              window.requestAnimationFrame(() => menuTriggerRef.current?.focus());
            }}
            data-testid="menu-dialog"
          >
            <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Navigate On A Lark, view event sources, and adjust settings.
            </DialogPrimitive.Description>
            <div
              className="rounded-[24px] border border-[#e8dfd2] bg-[rgba(253,251,247,0.98)] px-4 py-4 shadow-[0_18px_45px_rgba(32,24,12,0.12)] backdrop-blur-md lg:px-6 lg:py-5"
            >
              {/* Close button at top right of menu */}
              <div className="mb-4 flex items-center justify-between border-b border-[#ece4d7] pb-3">
                <div className="px-1">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#786f60]">
                    Menu
                  </p>
                </div>
                <Button
                  ref={menuCloseButtonRef}
                  variant="outline"
                  onClick={() => setMenuOpen(false)}
                  data-testid="menu-close-button"
                  aria-label="Close menu"
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-[#ddd2c2] bg-white/90 px-4 text-[14px] font-semibold text-foreground shadow-[0_2px_10px_rgba(0,0,0,0.06)] transition-colors hover:bg-[#f7f3eb]"
                >
                  <X className="h-4 w-4" />
                  <span>Close</span>
                </Button>
              </div>
              
              <div className="space-y-4">
                {/* Navigation Group */}
                <div className="rounded-[20px] border border-[#e7e0d3] bg-white/82 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                  <p className="px-1 pb-3 text-[11px] font-black uppercase tracking-[0.18em] text-[#786f60]">Navigation</p>
                  
                  {/* Stats Card - inside Navigation */}
                  {stats && isIconEnabled('stats') && (
                    <button
                      className="relative mb-3 w-full rounded-[18px] border border-[#9fd7bf] bg-[linear-gradient(135deg,#dff5ea_0%,#cceee0_58%,#b8e3cf_100%)] p-4 text-left text-[#134836] shadow-[0_10px_24px_rgba(29,158,117,0.14)] transition-transform duration-150 hover:-translate-y-[1px]"
                      style={{
                        backgroundBlendMode: 'normal',
                      }}
                      onClick={() => {
                        setLocation('/');
                        if (onExploreModeChange) {
                          onExploreModeChange('sources');
                        }
                        setMenuOpen(false);
                      }}
                      data-testid="menu-link-stats"
                    >
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="h-4 w-4 text-[#1a6644]" />
                          <span className="text-[11px] font-black uppercase tracking-[0.18em] text-[#2d6d54]">Event Stats</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[32px] font-black leading-none text-[#14553f]">{stats.eventCount.toLocaleString()}</span>
                          <span className="text-sm font-medium text-[#2d6d54]">events from {stats.sourceCount} sources</span>
                        </div>
                        <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#1a6644]">
                          <span>View Sources</span>
                          <ChevronRight className="h-3 w-3" />
                        </div>
                      </div>
                    </button>
                  )}
                  
                  <div className="space-y-1">
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                      onClick={() => {
                        setLocation('/');
                        setMenuOpen(false);
                      }}
                      data-testid="menu-link-home"
                    >
                      <Home className="h-4 w-4" />
                      Home
                    </Button>
                    {onExploreModeChange && (
                      <>
                        <Button
                          variant="ghost"
                          className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                          onClick={() => {
                            setLocation('/');
                            onExploreModeChange('sources');
                            setMenuOpen(false);
                          }}
                          data-testid="menu-link-local-calendars"
                        >
                          <Layers3 className="h-4 w-4" />
                          Browse local calendars
                        </Button>
                        <Button
                          variant="ghost"
                          className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                          onClick={() => {
                            setLocation('/');
                            onExploreModeChange('recently-viewed');
                            setMenuOpen(false);
                          }}
                          data-testid="menu-link-recently-viewed"
                        >
                          <Clock3 className="h-4 w-4" />
                          <span className="flex-1 text-left">Recently viewed</span>
                          {recentlyViewedCount > 0 && (
                            <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-[#e4ece7] px-2 py-0.5 text-xs text-[#315f4e]">
                              {recentlyViewedCount}
                            </span>
                          )}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                      onClick={() => {
                        setLocation('/about');
                        setMenuOpen(false);
                      }}
                      data-testid="menu-link-about"
                    >
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                      About · Source Appreciation
                    </Button>
                  
                  {isIconEnabled('contact') && (
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                      onClick={() => {
                        setLocation('/contact');
                        setMenuOpen(false);
                      }}
                      data-testid="menu-link-contact"
                    >
                      <Mail className="h-4 w-4" />
                      Contact
                    </Button>
                  )}
                  
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                      onClick={() => {
                        setLocation('/admin');
                        setMenuOpen(false);
                      }}
                      data-testid="menu-link-admin"
                    >
                      <BadgeInfo className="h-4 w-4" />
                      Admin Panel
                    </Button>
                  )}
                  </div>
                </div>
                
                {/* Settings Group */}
                <div className="rounded-[20px] border border-[#e7e0d3] bg-white/82 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                  <p className="px-1 pb-3 text-[11px] font-black uppercase tracking-[0.18em] text-[#786f60]">Settings</p>

                  {/* Auto Scroll toggle */}
                  {onAutoScrollToggle && (
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                      onClick={() => {
                        onAutoScrollToggle();
                      }}
                      data-testid="menu-button-autoscroll"
                    >
                      {isAutoScrollPlaying ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      Auto Scroll {isAutoScrollPlaying ? '(On)' : '(Off)'}
                    </Button>
                  )}

                  {isIconEnabled('fontSize') && (
                    <div className="mt-1 rounded-[14px] px-2 py-1 text-[15px] font-semibold text-foreground transition-colors hover:bg-[#f4f0e7]">
                      <FontSizeControl variant="menu-row" label="Text size" />
                    </div>
                  )}

                </div>

                {/* Account Group - Only show for authenticated users */}
                {isAuthenticated && (
                  <div className="rounded-[20px] border border-[#e7e0d3] bg-white/82 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                    <p className="px-1 pb-3 text-[11px] font-black uppercase tracking-[0.18em] text-[#786f60]">Account</p>
                    
                    <div className="mb-1 flex items-center gap-3 rounded-[14px] bg-[#f7f3eb] px-4 py-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                        <span className="text-sm font-semibold text-primary">
                          {(user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{user?.firstName || user?.email || 'User'}</p>
                        <p className="text-xs text-muted-foreground">Signed in</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-foreground hover:bg-[#f4f0e7]"
                      onClick={() => {
                        setLocation('/my-larks');
                        setMenuOpen(false);
                      }}
                      data-testid="menu-link-my-larks"
                    >
                      <Heart className="h-4 w-4" />
                      My Larks
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-12 w-full justify-start gap-3 rounded-[14px] px-4 text-[15px] font-semibold text-destructive hover:bg-[#fdf0ee] hover:text-destructive"
                      onClick={() => {
                        setMenuOpen(false);
                        signOut({ redirectUrl: basePath || "/" });
                      }}
                      data-testid="menu-link-logout"
                    >
                      <LogOut className="h-4 w-4" />
                      Log Out
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </header>
    </>
  );
}

export { type DateFilterType } from "./FiltersPanel";
